// src/native/compliance/aiConsentStore.js
//
// WS-7 COMPLIANCE. Reads and writes the two consent records:
//
//   AI data sharing   App Store Guideline 5.1.2(i)
//   Minors            App Store Guideline 5.1.4(b)
//
// Every public function here is safe to call on the web. When
// isAiConsentRequiredHere() is false, the readers report "not required" and the
// gate never renders, so no web behaviour changes.

import posthog from 'posthog-js';
import { supabase } from '@/lib/supabase';
import {
  AI_PROCESSOR_IDS,
  CONSENT_LOCAL_KEY,
  CONSENT_METADATA_KEY,
  CONSENT_PREFS_KEY,
  CONSENT_VERSION,
  MINORS_ACK_VERSION,
  MINORS_LOCAL_KEY,
  MINORS_METADATA_KEY,
  MINORS_PREFS_KEY,
  MINORS_ROLE_AFFIRMED,
  currentPlatformTag,
  isAiConsentRequiredHere,
  localIsoTimestamp,
} from './consentConfig';
import { readDurable, readLocal, writeDurable } from './complianceStorage';

const CONSENT_KEYS = { localKey: CONSENT_LOCAL_KEY, prefsKey: CONSENT_PREFS_KEY };
const MINORS_KEYS = { localKey: MINORS_LOCAL_KEY, prefsKey: MINORS_PREFS_KEY };

/**
 * Fired when something wanted to send data to an AI processor and consent was
 * not in place. AiConsentGate listens and ARMS itself: the gate renders nothing
 * until this event has fired at least once, which is what makes the consent
 * screen appear on first AI use rather than on app open. Kept as a window event
 * so a call site can guard itself with two lines and no new context.
 */
export const AI_CONSENT_REQUIRED_EVENT = 'ttt:ai-consent-required';

/** Fired after any consent change so open screens refresh without a reload. */
export const AI_CONSENT_CHANGED_EVENT = 'ttt:ai-consent-changed';

/**
 * THE REFUSAL CONTRACT.
 *
 * A guard that returns undefined out of an async function is indistinguishable
 * from success: the promise resolves, the caller's catch never runs, and its
 * "it worked" branch executes on a send that never happened. That is how a
 * refused TTS call reported success to the tile board and locked the Speak
 * button for five seconds without saying a word.
 *
 * So the contract has two halves, and every call site uses exactly one of them:
 *
 *   SYNCHRONOUS CALL SITES call isAiSharingAllowed() and branch themselves.
 *   The check must sit ABOVE every side effect: before the message is appended,
 *   before it is written to the database, before the input is cleared, before
 *   any state machine is moved. Refusing after a write leaves the user staring
 *   at their own message with no reply and no explanation of either.
 *
 *   ASYNC WRAPPERS around a send REJECT with this error. Never resolve. Every
 *   existing caller already treats a rejection as "the audio did not play" or
 *   "the request failed", which is precisely true, so their own failure UI runs
 *   and no state machine is left hanging.
 */
export class AiConsentRequiredError extends Error {
  constructor(message = 'AI data sharing has not been agreed to') {
    super(message);
    this.name = 'AiConsentRequiredError';
    /** Lets a caller tell this apart from a network failure without string matching. */
    this.aiConsentRequired = true;
  }
}

/**
 * The async half of the contract: returns nothing when sharing is allowed,
 * throws AiConsentRequiredError when it is not (and arms the gate on the way).
 *
 * Use this at the top of any async function that reaches a processor, in place
 * of a bare `if (!isAiSharingAllowed()) return`.
 */
export function assertAiSharingAllowed(options) {
  if (isAiSharingAllowed(options)) return;
  throw new AiConsentRequiredError();
}

function emit(name, detail) {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    /* non-fatal */
  }
}

function track(event, props) {
  try {
    posthog.capture(event, props);
  } catch {}
}

// ---------------------------------------------------------------------------
// Shape helpers
// ---------------------------------------------------------------------------

function isCurrentConsent(record) {
  return !!record && record.granted === true && Number(record.version) === CONSENT_VERSION;
}

function isCurrentMinorsAck(record) {
  return !!record && record.acknowledged === true && Number(record.version) === MINORS_ACK_VERSION;
}

// ---------------------------------------------------------------------------
// Synchronous first-paint read
// ---------------------------------------------------------------------------

// The in-memory tier. Written by setAiConsent / setMinorsAck alongside the
// durable tiers, and read here whenever localStorage has nothing.
//
// This exists for Safari private mode, a full quota and a locked-down webview:
// writeLocal() swallows those failures and returns false, so before this the
// user could tap "I agree", have the write silently fail, and meet the same
// screen again on their very next AI action, forever, with no way through. The
// decision is real and explicit either way; only its storage failed, and an
// answer we are holding in memory is still an answer for the rest of the
// session. It is deliberately not a substitute for the durable tiers: a reload
// clears it and the user is asked once more, which is the correct outcome when
// we genuinely cannot remember what they said.
let memoryConsent = null;
let memoryMinors = null;

/**
 * Synchronous best guess from the local tiers alone, used to decide whether the
 * gate should paint. The async readers below are authoritative. Returning
 * "granted" here when the durable tiers disagree is harmless: the async pass
 * corrects it within a tick and the gate re-renders.
 */
export function peekConsentSync() {
  if (!isAiConsentRequiredHere()) {
    return { required: false, granted: true, decided: true, minorsDone: true };
  }
  const consent = readLocal(CONSENT_LOCAL_KEY) || memoryConsent;
  const minors = readLocal(MINORS_LOCAL_KEY) || memoryMinors;
  return {
    required: true,
    granted: isCurrentConsent(consent),
    decided: !!consent && Number(consent.version) === CONSENT_VERSION,
    minorsDone: isCurrentMinorsAck(minors),
  };
}

// ---------------------------------------------------------------------------
// Authoritative async read
// ---------------------------------------------------------------------------

async function readMetadata(key) {
  try {
    const { data } = await supabase.auth.getUser();
    const value = data?.user?.user_metadata?.[key];
    return value && typeof value === 'object' ? value : null;
  } catch {
    // Signed out, offline, or an auth hiccup. The durable tiers still answer.
    return null;
  }
}

async function writeMetadata(key, record) {
  try {
    const { data } = await supabase.auth.getUser();
    if (!data?.user) return false;
    const { error } = await supabase.auth.updateUser({ data: { [key]: record } });
    return !error;
  } catch {
    return false;
  }
}

/**
 * Reads the AI consent record from every tier and returns the authoritative
 * answer. Also repairs tiers that are behind, so a user who agreed on another
 * device is not asked again after signing in here.
 *
 * @returns {Promise<{required: boolean, granted: boolean, decided: boolean, record: object|null}>}
 */
export async function getAiConsent() {
  if (!isAiConsentRequiredHere()) {
    return { required: false, granted: true, decided: true, record: null };
  }

  const [{ record: durable }, remote] = await Promise.all([
    readDurable(CONSENT_KEYS),
    readMetadata(CONSENT_METADATA_KEY),
  ]);

  // The account copy wins when it is current and the device copy is not. That is
  // the reinstall case. In every other case the device copy is at least as new.
  let winner = durable;
  if (isCurrentConsent(remote) && !isCurrentConsent(durable)) winner = remote;

  if (winner && winner !== durable) {
    await writeDurable(CONSENT_KEYS, winner);
  }
  // Seed the memory tier too, so the synchronous guard agrees with this answer
  // even when the durable write failed (private mode) or the record only
  // existed on the account, as it does after a reinstall.
  if (winner) memoryConsent = winner;

  return {
    required: true,
    granted: isCurrentConsent(winner),
    decided: !!winner && Number(winner.version) === CONSENT_VERSION,
    record: winner || null,
  };
}

/** Same, for the minors acknowledgement. */
export async function getMinorsAck() {
  if (!isAiConsentRequiredHere()) {
    return { required: false, acknowledged: true, record: null };
  }

  const [{ record: durable }, remote] = await Promise.all([
    readDurable(MINORS_KEYS),
    readMetadata(MINORS_METADATA_KEY),
  ]);

  let winner = durable;
  if (isCurrentMinorsAck(remote) && !isCurrentMinorsAck(durable)) winner = remote;
  if (winner && winner !== durable) await writeDurable(MINORS_KEYS, winner);
  if (winner) memoryMinors = winner;

  return {
    required: true,
    acknowledged: isCurrentMinorsAck(winner),
    record: winner || null,
  };
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * Records an explicit consent decision.
 *
 * `granted` must come from a real tap on a real button. There is no pre-ticked
 * box and no implied consent on continue, because 5.1.2(i) asks for explicit
 * permission and a reviewer will look for exactly that.
 *
 * @param {boolean} granted
 * @param {{source?: string}} [meta] where the decision was made, for analytics
 */
export async function setAiConsent(granted, meta = {}) {
  const record = {
    version: CONSENT_VERSION,
    granted: granted === true,
    at: localIsoTimestamp(),
    platform: currentPlatformTag(),
    processors: AI_PROCESSOR_IDS,
    source: meta.source || 'consent_screen',
  };

  // Memory first, and synchronously, so the decision is already readable by
  // peekConsentSync before any storage tier has had a chance to fail.
  memoryConsent = record;
  await writeDurable(CONSENT_KEYS, record);
  // Fire and forget: a signed-out user has no account tier, and a network
  // failure must not lose a decision the user already made on this device.
  void writeMetadata(CONSENT_METADATA_KEY, record);

  // Three distinct names so the funnel impact is legible in PostHog without
  // touching or renaming any existing funnel event. None of these names existed
  // in the repo before WS-7.
  let eventName = 'ai_consent_accepted';
  if (!granted) {
    eventName = meta.source === 'settings' ? 'ai_consent_revoked_in_settings' : 'ai_consent_declined';
  }
  track(eventName, {
    platform: record.platform,
    consent_version: CONSENT_VERSION,
    source: record.source,
  });

  emit(AI_CONSENT_CHANGED_EVENT, { granted: record.granted });
  return record;
}

/**
 * The one tap accept path used by the gate.
 *
 * Writes the minors affirmation and the AI consent together, because on the
 * screen they are a single explicit act: the sentence above the button states
 * that the person tapping is a parent, guardian or carer, or is 16 or older.
 * Splitting them into two taps would cost funnel conversion and add no legal
 * weight.
 */
export async function acceptAiConsentWithAffirmation(meta = {}) {
  const minors = await setMinorsAck({ role: MINORS_ROLE_AFFIRMED, silent: true });
  const consent = await setAiConsent(true, meta);
  return { consent, minors };
}

/**
 * Records the minors affirmation.
 *
 * @param {{role: string, silent?: boolean}} answer
 *   silent suppresses the change event, so acceptAiConsentWithAffirmation can
 *   write both records and fire exactly one re-render at the end.
 */
export async function setMinorsAck(answer) {
  const record = {
    version: MINORS_ACK_VERSION,
    acknowledged: true,
    role: answer.role,
    at: localIsoTimestamp(),
    platform: currentPlatformTag(),
  };

  memoryMinors = record;
  await writeDurable(MINORS_KEYS, record);
  void writeMetadata(MINORS_METADATA_KEY, record);

  track('minors_affirmation_recorded', {
    role: record.role,
    platform: record.platform,
  });

  if (!answer.silent) emit(AI_CONSENT_CHANGED_EVENT, { minorsDone: true });
  return record;
}

/**
 * Copies a locally stored decision up to the account after sign-in. Cheap, safe
 * to call more than once, and the reason a user who agreed on the gate before
 * signing in is not asked twice.
 */
export async function syncConsentToAccount() {
  if (!isAiConsentRequiredHere()) return;
  const consent = readLocal(CONSENT_LOCAL_KEY);
  if (consent) void writeMetadata(CONSENT_METADATA_KEY, consent);
  const minors = readLocal(MINORS_LOCAL_KEY);
  if (minors) void writeMetadata(MINORS_METADATA_KEY, minors);
}

// ---------------------------------------------------------------------------
// The guard
// ---------------------------------------------------------------------------

/**
 * Synchronous permission check for any code path about to send speech or
 * conversation text to a third party. Reads localStorage only, so it is safe to
 * call on a hot path.
 *
 * With AI_CONSENT_GATE_SCOPE at 'both' this is a real check on the web as well
 * as on native. It returns true for every user who has accepted, which after
 * first run is effectively everyone, so the common path is one localStorage read
 * and a comparison.
 *
 * When it returns false it also arms the gate, so the consent screen appears
 * right here, on the screen the user is on, immediately before the send it just
 * stopped. That is the whole retiming: nothing blocks the app on open, and the
 * ask lands at the one moment it is legally required.
 *
 * Two consequences every caller must respect:
 *   - Call this IMMEDIATELY BEFORE the send, inside the user's action. Calling
 *     it on mount, on a timer, or in a prefetch would put the consent screen in
 *     front of someone who never asked for an AI feature.
 *   - Handle false as "this send did not happen". The gate is already on screen
 *     explaining why, so do not also raise an error toast, and above all do not
 *     leave the caller stuck in a loading or listening state with no way out.
 *
 * Pass { prompt: false } for anything speculative (a warm-up, a prefetch, a
 * capability probe): it answers the question without putting a screen in front
 * of anybody.
 */
export function isAiSharingAllowed({ prompt = true } = {}) {
  const state = peekConsentSync();
  if (!state.required) return true;
  if (state.granted && state.minorsDone) return true;
  if (prompt) emit(AI_CONSENT_REQUIRED_EVENT, {});
  return false;
}
